import { useState, type FormEvent } from "react";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, ApiError, type EstadoTarea, type FotoNovedad, type Novedad } from "@/lib/api";
import { aPayloadNovedad, aPayloadNovedadNueva, formNovedad, hayErroresNovedad, MAX_FOTOS, validarFoto, validarNovedad, type ErroresNovedad, type FormNovedad } from "@/lib/avance";

const SIN_ERRORES: ErroresNovedad = { estados: {} };

export interface FotoPendiente {
  clave: string;
  archivo: File;
  epigrafe: string;
}

let siguiente = 0;

/** Para reusar el formulario al marcar un sub-hito como cumplido: el alta va a otro endpoint (que arma el título). */
export interface OpcionesFormularioNovedad {
  titulo?: string;
  crear?: (form: FormNovedad) => Promise<Novedad>;
  /** El "Avisar" de entrada (por defecto, el del formulario vacío). */
  avisar?: boolean;
}

/**
 * Lógica del formulario de una novedad (sin JSX). Guardar tiene dos pasos: primero la novedad (POST o PATCH) y
 * después las fotos elegidas, de a una. Si una foto falla, la novedad ya quedó guardada: se recuerda su id para que
 * el reintento la edite en vez de crear otra, y las fotos que sí subieron salen de la lista de pendientes.
 */
export function useFormularioNovedad(fid: number, novedad: Novedad | null, alGuardar: () => void, opciones: OpcionesFormularioNovedad = {}) {
  const base = `/api/fideicomisos/${fid}/avance`;
  const [form, setForm] = useState<FormNovedad>(() => {
    const inicial = formNovedad(novedad ?? undefined);
    const conTitulo = opciones.titulo === undefined ? inicial : { ...inicial, titulo: opciones.titulo };
    return opciones.avisar === undefined ? conTitulo : { ...conTitulo, avisar: opciones.avisar };
  });
  const [guardadaId, setGuardadaId] = useState<number | null>(novedad?.id ?? null);
  const [fotos, setFotos] = useState<FotoNovedad[]>(novedad?.fotos ?? []);
  const [pendientes, setPendientes] = useState<FotoPendiente[]>([]);
  const [errorFotos, setErrorFotos] = useState<string | null>(null);
  const [mostrarErrores, setMostrarErrores] = useState(false);

  const errores = validarNovedad(form);
  const lugares = MAX_FOTOS - fotos.length - pendientes.length;

  const accionGuardar = useAsyncAction(async () => {
    const guardada =
      guardadaId === null
        ? await (opciones.crear ? opciones.crear(form) : api.post<Novedad>(`${base}/novedades`, aPayloadNovedadNueva(form)))
        : await api.patch<Novedad>(`${base}/novedades/${guardadaId}`, aPayloadNovedad(form));
    setGuardadaId(guardada.id);
    for (const p of pendientes) {
      const datos = new FormData();
      datos.append("archivo", p.archivo);
      if (p.epigrafe.trim()) datos.append("epigrafe", p.epigrafe.trim());
      try {
        const subida = await api.upload<FotoNovedad>(`${base}/novedades/${guardada.id}/fotos`, datos);
        setFotos((f) => [...f, subida]);
        setPendientes((lista) => lista.filter((x) => x.clave !== p.clave));
      } catch (e) {
        const motivo = e instanceof ApiError ? e.message : "error inesperado";
        throw new ApiError(e instanceof ApiError ? e.status : 0, `La novedad se guardó, pero no se pudo subir ${p.archivo.name}: ${motivo}`);
      }
    }
    return true;
  });

  const accionBorrarFoto = useAsyncAction((foto: FotoNovedad) => api.delete<void>(`${base}/fotos/${foto.id}`).then(() => foto.id));

  const elegirFotos = (archivos: File[]) => {
    setErrorFotos(null);
    let libres = lugares;
    const nuevas: FotoPendiente[] = [];
    for (const archivo of archivos) {
      const invalido = validarFoto(archivo);
      if (invalido) {
        setErrorFotos(invalido);
        continue;
      }
      if (libres <= 0) {
        setErrorFotos(`Una novedad admite hasta ${MAX_FOTOS} fotos`);
        break;
      }
      libres -= 1;
      nuevas.push({ clave: `foto-${(siguiente += 1)}`, archivo, epigrafe: "" });
    }
    if (nuevas.length > 0) setPendientes((lista) => [...lista, ...nuevas]);
  };

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    setErrorFotos(null);
    accionBorrarFoto.limpiarError();
    if (hayErroresNovedad(errores)) return;
    if (await accionGuardar.run()) alGuardar();
  };

  return {
    form,
    cambiar: <K extends "fecha" | "titulo" | "texto" | "avisar">(campo: K, valor: FormNovedad[K]) => setForm((f) => ({ ...f, [campo]: valor })),
    /** Agrega una tarea a la novedad, sin estado elegido todavía. */
    agregarCambio: (etapaId: string) =>
      setForm((f) => (f.cambios.some((c) => c.etapa_id === etapaId) ? f : { ...f, cambios: [...f.cambios, { etapa_id: etapaId, estado: "" }] })),
    cambiarEstado: (etapaId: string, estado: EstadoTarea) =>
      setForm((f) => ({ ...f, cambios: f.cambios.map((c) => (c.etapa_id === etapaId ? { ...c, estado } : c)) })),
    quitarCambio: (etapaId: string) => setForm((f) => ({ ...f, cambios: f.cambios.filter((c) => c.etapa_id !== etapaId) })),
    errores: mostrarErrores ? errores : SIN_ERRORES,
    fotos,
    borrarFoto: async (foto: FotoNovedad) => {
      const id = await accionBorrarFoto.run(foto);
      if (id !== undefined) setFotos((f) => f.filter((x) => x.id !== id));
    },
    pendientes,
    elegirFotos,
    quitarPendiente: (clave: string) => setPendientes((lista) => lista.filter((p) => p.clave !== clave)),
    cambiarEpigrafe: (clave: string, epigrafe: string) => setPendientes((lista) => lista.map((p) => (p.clave === clave ? { ...p, epigrafe } : p))),
    errorFotos: errorFotos ?? accionBorrarFoto.error,
    lugares,
    guardar,
    guardando: accionGuardar.loading,
    errorGuardar: accionGuardar.error,
  };
}
